// Public "Claim your store" directory. Browse pre-populated business listings
// (seeded from OpenStreetMap) on a map + searchable list; a real owner clicks
// "Claim this store" and lands on a prefilled merchant registration.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ShoppingBag, Search, MapPin, Store, Pill, Apple, ArrowRight, Loader2 } from 'lucide-react';
import { listings, type MerchantListing } from '../lib/api';
import { MapView } from '../components/shared/MapView';
import { Button } from '../components/stripe-ui/Button';

const CATEGORIES = [
  { value: '',          label: 'All' },
  { value: 'groceries', label: 'Groceries' },
  { value: 'pharmacy',  label: 'Pharmacies' },
  { value: 'fresh',     label: 'Markets' },
];

const TYPE_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  pharmacy: Pill, marketplace: Apple, supermarket: Store, convenience: Store,
};
// Merchant Join's TYPES map: groceries deep blue, pharmacy violet, markets
// green. Groceries was the INTERACTIVE blue (#1455E8) rather than the deep
// brand blue the design pins to a store pin.
const CAT_COLOR: Record<string, string> = { pharmacy: '#5B21B6', fresh: '#0A6B45', groceries: '#063B9E' };

export function ClaimStorePage() {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const listQ = useQuery({ queryKey: ['listings', 'all'], queryFn: () => listings.list({ limit: 500 }) });

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (listQ.data ?? []).filter((l) =>
      (!cat || l.category_slug === cat) &&
      (!term || l.name.toLowerCase().includes(term) || (l.address ?? '').toLowerCase().includes(term) || (l.city?.name ?? '').toLowerCase().includes(term)));
  }, [listQ.data, q, cat]);

  const markers = useMemo(() => filtered.slice(0, 250).map((l) => ({
    lat: Number(l.latitude), lng: Number(l.longitude),
    label: `${l.name}${l.city?.name ? ` · ${l.city.name}` : ''}`,
    color: CAT_COLOR[l.category_slug ?? ''] ?? '#1455E8',
  })), [filtered]);

  return (
    <div className="min-h-screen bg-white text-[var(--color-ink)] flex flex-col">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-[var(--color-hairline)]">
        <div className="mx-auto max-w-7xl px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 text-white"><ShoppingBag className="w-4 h-4" /></span>
            <span className="font-semibold tracking-tight text-[17px]">VoyeLakay</span>
          </Link>
          <div className="flex items-center gap-3 text-[13px]">
            <Link to="/login" className="text-[var(--color-secondary)] hover:text-[var(--color-ink)] font-medium">Sign in</Link>
            <Link to="/register?role=merchant" className="px-3 py-1.5 rounded-md bg-[var(--color-ink)] text-white font-semibold hover:bg-[var(--color-primary)]">Register</Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl w-full px-6 py-8 flex-1">
        <div className="max-w-2xl">
          <h1 className="text-[26px] sm:text-[34px] font-bold tracking-tight">Own a store in Haiti? Claim your listing.</h1>
          <p className="mt-2 text-[14px] text-[var(--color-secondary)]">
            We've mapped thousands of local supermarkets, pharmacies, and markets from public data. Find yours, claim it, and start fulfilling orders for the diaspora — your details are pre-filled, you just confirm and submit for verification.
          </p>
        </div>

        {/* Search + filters */}
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-muted)]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by store name, address, or city…"
              className="w-full pl-9 pr-3 py-2.5 text-[13.5px] border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            />
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {CATEGORIES.map((c) => (
              <button key={c.value} onClick={() => setCat(c.value)}
                className={`px-3 py-1.5 rounded-full text-[12px] font-semibold transition-colors ${cat === c.value ? 'bg-[var(--color-ink)] text-white' : 'bg-[var(--color-panel)] text-[var(--color-body)] hover:bg-[var(--color-border)]'}`}>
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {listQ.isLoading ? (
          <div className="text-center py-20 text-[13px] text-[var(--color-muted)]"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading listings…</div>
        ) : (
          <>
            <p className="mt-4 text-[12px] text-[var(--color-secondary-2)]">{filtered.length} unclaimed {filtered.length === 1 ? 'store' : 'stores'}{cat ? ` · ${CATEGORIES.find((c) => c.value === cat)?.label}` : ''}</p>

            {markers.length > 0 && (
              <div className="mt-3 rounded-xl overflow-hidden border border-[var(--color-border)]">
                <MapView height="360px" markers={markers} />
              </div>
            )}

            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filtered.slice(0, 120).map((l) => <ListingCard key={l.id} l={l} />)}
            </div>
            {filtered.length > 120 && <p className="mt-4 text-[12px] text-[var(--color-muted)]">Showing the first 120. Refine your search to find your store.</p>}
            {filtered.length === 0 && (
              <div className="mt-6 text-center py-12 text-[13px] text-[var(--color-secondary-2)]">
                No matching store. <Link to="/register?role=merchant" className="text-brand-600 font-semibold hover:underline">Register it manually →</Link>
              </div>
            )}
          </>
        )}

        <div className="mt-10 rounded-xl bg-[var(--color-panel)] border border-[var(--color-border)] p-5 text-center">
          <p className="text-[14px] font-semibold text-[var(--color-ink)]">Don't see your store?</p>
          <p className="text-[12.5px] text-[var(--color-secondary)] mt-1">Register it from scratch — it only takes a minute.</p>
          <Link to="/register?role=merchant" className="inline-flex items-center gap-1.5 mt-3 px-4 py-2 rounded-md bg-[var(--color-ink)] text-white text-[13px] font-semibold hover:bg-[var(--color-primary)]">
            Register your store <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </main>
    </div>
  );
}

function ListingCard({ l }: { l: MerchantListing }) {
  const Icon = TYPE_ICON[l.osm_type ?? ''] ?? Store;
  return (
    <div className="rounded-xl border border-[var(--color-border)] p-4 flex flex-col hover:border-[var(--color-border)] transition-colors">
      <div className="flex items-start gap-2.5">
        <span className="inline-flex w-9 h-9 items-center justify-center rounded-lg bg-brand-50 text-brand-600 shrink-0"><Icon className="w-4.5 h-4.5" /></span>
        <div className="min-w-0">
          <p className="text-[13.5px] font-semibold text-[var(--color-ink)] truncate">{l.name}</p>
          <p className="text-[11.5px] text-[var(--color-secondary-2)] capitalize">{(l.osm_type ?? l.category_slug ?? 'store').replace('_', ' ')}</p>
        </div>
      </div>
      <p className="mt-2 text-[12px] text-[var(--color-secondary)] inline-flex items-start gap-1">
        <MapPin className="w-3.5 h-3.5 text-[var(--color-muted)] mt-0.5 shrink-0" />
        <span>{l.address || l.city?.name || 'Haiti'}{l.address && l.city?.name ? ` · ${l.city.name}` : ''}</span>
      </p>
      <div className="mt-3 pt-3 border-t border-[var(--color-hairline)]">
        <Link to={`/register?role=merchant&listing=${l.id}`}>
          <Button size="sm" className="w-full">Claim this store</Button>
        </Link>
      </div>
    </div>
  );
}
